import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Archive, ArrowLeft } from 'lucide-react';

const YearContext = createContext(null);

export function useYearContext() {
  const context = useContext(YearContext);
  if (!context) throw new Error('Stránka musí být uvnitř <Layout>');
  return context;
}

const NAV = [
  { to: '', label: 'O předmětu', end: true },
  { to: 'hodiny', label: 'Hodiny' },
  { to: 'znamkovani', label: 'Známkování' },
  { to: 'ukoly', label: 'Úkoly' },
  { to: 'materialy', label: 'Materiály' },
];

function Nav({ basePath, className, onNavigate }) {
  const href = (to) => (to ? `${basePath}/${to}` : basePath || '/');

  return (
    <nav className={className} aria-label="Hlavní navigace">
      <ul>
        {NAV.map((item) => (
          <li key={item.to}>
            <NavLink to={href(item.to)} end={item.end} className="nav-link" onClick={onNavigate}>
              {item.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function Layout({ year, archived = false, basePath = '', children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // menu se zavře při přechodu na jinou stránku
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // ...a při zvětšení okna na šířku, kde už je vidět plná navigace
  useEffect(() => {
    if (!menuOpen) return undefined;
    const media = window.matchMedia('(min-width: 46rem)');
    const close = () => setMenuOpen(false);
    media.addEventListener('change', close);
    return () => media.removeEventListener('change', close);
  }, [menuOpen]);

  useEffect(() => {
    const onKeyDown = (event) => { if (event.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <YearContext.Provider value={{ year, archived, basePath }}>
      <a className="skip-link" href="#obsah">Přeskočit na obsah</a>

      <header className="site-header">
        <div className="shell header-bar">
          <Link to={basePath || '/'} className="brand">
            <span><span className="brand-mark">/</span> Seminář z informatiky</span>
            {year && <span className="brand-year">{year.label}</span>}
          </Link>

          <Nav basePath={basePath} className="nav-desktop" />

          <button
            type="button"
            className="menu-toggle"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls="mobilni-menu"
            aria-label={menuOpen ? 'Zavřít menu' : 'Otevřít menu'}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <div className="shell">
          <Nav
            basePath={basePath}
            className={`nav-mobile${menuOpen ? ' is-open' : ''}`}
            onNavigate={() => setMenuOpen(false)}
          />
        </div>
      </header>

      {archived && year && (
        <div className="archive-banner">
          <div className="shell archive-banner-inner">
            <Archive size={16} aria-hidden="true" />
            <span>Prohlížíte si archiv školního roku <strong>{year.label}</strong>.</span>
            <Link to="/">Přejít na aktuální ročník</Link>
          </div>
        </div>
      )}

      <main id="obsah" className="shell page">{children}</main>

      <footer className="site-footer">
        <div className="shell footer-inner">
          <span>
            {year?.school || 'Gymnázium Nad Kavalírkou'}
            {year?.teacher ? ` · ${year.teacher}` : ''}
          </span>
          <div className="footer-links">
            {archived
              ? <Link to="/"><ArrowLeft size={14} style={{ verticalAlign: '-2px' }} /> Aktuální ročník</Link>
              : <Link to="/archiv">Archiv předchozích ročníků</Link>}
            <Link to="/admin">Správa</Link>
          </div>
        </div>
      </footer>
    </YearContext.Provider>
  );
}

export default Layout;
